import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/queries/useUser';
import { useStoreData } from '@/queries/useStoreData';
import { useStanRouter } from '@/hooks/useStanRouter';
import { mainApi } from '@/lib/api';
import { stanNotify } from '@/lib/stanNotify';
import { INTEGRATION_STATUS, QUERY_KEYS, STRIPE_ACCOUNT_STATUS } from '@/lib/constants';
import { IntegrationData } from '@/types/user';
import { isAxiosError } from 'axios';
import { BannerButton } from '@/components/core-components/StanBanner';

// Helper function to create centered popup window
function createCenteredPopupWin(url: string, title: string, width: number, height: number): Window | null {
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    return window.open(
        url,
        title,
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );
}

const FORMATTED_INTEGRATION_NAMES: Record<string, string> = {
    'google-calendar': 'Google Calendar',
    zoom: 'Zoom',
    'stripe-connect': 'Stripe',
    paypal: 'PayPal',
};

export function useIntegrationBanners() {
    const { user } = useUser();
    const { push } = useStanRouter();
    const queryClient = useQueryClient();
    const { data: storeData } = useStoreData(user?.default_store_id);
    const [loading, setLoading] = useState(false);

    // Get integrations from user - return early if no user
    const integrations = user?.integrations || {};

    // Check Stripe account issues
    const stripeIntegration = integrations['stripe-connect'] as IntegrationData | undefined;
    const hasStripeAccountIssues = useMemo(() => {
        return (
            stripeIntegration?.data?.account_status &&
            [
                STRIPE_ACCOUNT_STATUS.REJECTED,
                STRIPE_ACCOUNT_STATUS.RESTRICTED,
                STRIPE_ACCOUNT_STATUS.RESTRICTED_SOON,
            ].includes(stripeIntegration.data.account_status)
        );
    }, [stripeIntegration]);

    const stripeAccountErrorBannerMessage = stripeIntegration?.data?.account_status_details;

    // Check PayPal account issues
    const paypalIntegration = integrations['paypal'] as IntegrationData | undefined;
    const isPaypalConnected = paypalIntegration?.status === INTEGRATION_STATUS.ACTIVE;
    const isPaypalChargesEnabled =
        paypalIntegration?.data?.payments_receivable && paypalIntegration?.data?.primary_email_confirmed;
    const isPaypalEmailConfirmed = paypalIntegration?.data?.primary_email_confirmed;
    const hasPaypalAccountIssues = isPaypalConnected && !isPaypalChargesEnabled;

    const paypalAccountErrorBannerMessage = useMemo(() => {
        if (hasPaypalAccountIssues && isPaypalEmailConfirmed) {
            return 'Your account connection is not complete.';
        }
        if (hasPaypalAccountIssues) {
            return 'To accept payments, please verify your PayPal email address.';
        }
        return '';
    }, [hasPaypalAccountIssues, isPaypalEmailConfirmed]);

    // Get errored integrations
    const erroredIntegrations = useMemo(() => {
        return Object.values(integrations)
            .filter((integration: IntegrationData) => integration.status === INTEGRATION_STATUS.ERRORED)
            .map((integration: IntegrationData) => integration.type);
    }, [integrations]);

    const formattedStringOfErroredIntegrations = useMemo(() => {
        const formattedNames = erroredIntegrations.map(
            (integration: string) => FORMATTED_INTEGRATION_NAMES[integration] || integration
        );
        if (formattedNames.length === 0) return '';
        if (formattedNames.length === 1) return formattedNames[0];
        const last = formattedNames.pop();
        return `${formattedNames.join(', ')} and ${last}`;
    }, [erroredIntegrations]);

    const missingIntegrationErrorBannerMessage = useMemo(() => {
        if (!formattedStringOfErroredIntegrations) return '';
        return `Please go to the Integrations tab to reconnect ${formattedStringOfErroredIntegrations}!`;
    }, [formattedStringOfErroredIntegrations]);

    // Check if Instagram integration is errored
    const isErroredIntegration = (integrationType: string) => {
        return erroredIntegrations.includes(integrationType);
    };

    // Check if product has errored integration
    const hasProductWithErroredIntegration = useMemo(() => {
        if (!storeData || storeData.length === 0) return false;
        const store = storeData[0];
        if (!store || !store.pages) return false;

        function checkIfProductHasErroredIntegration(product: any) {
            if (!product) return false;
            switch (product?.type) {
                case 'meeting':
                    return erroredIntegrations.includes(product.meeting?.platform);
                default:
                    return false;
            }
        }

        for (const page of store.pages) {
            if (checkIfProductHasErroredIntegration(page.data.product)) {
                return true;
            }
        }
        return false;
    }, [storeData, erroredIntegrations]);

    // Navigation functions
    const navigateToIntegrations = () => {
        push('/account_settings?tab=integrations');
    };

    const navigateToPayments = () => {
        push('/account_settings?tab=store');
    };

    // Stripe account management
    const manageStripeAccount = async () => {
        setLoading(true);
        try {
            const response = await mainApi.post<{ url: string }>('v1/integrations/stripe/connect', {});
            const authPopupWindow = createCenteredPopupWin(response.data.url, 'Connect Stripe Integration', 500, 800);
            setLoading(false);

            if (authPopupWindow) {
                const timer = setInterval(() => {
                    if (authPopupWindow.closed) {
                        // Refetch user data to update integration status
                        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER] });
                        clearInterval(timer);
                    }
                }, 300);
            }
        } catch (error: unknown) {
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: isAxiosError(error)
                    ? (error.response?.data?.message ?? 'Please try again later.')
                    : 'Please try again later.',
            });
            setLoading(false);
        }
    };

    // PayPal account management
    const managePaypalAccount = () => {
        const authPopupWindow = createCenteredPopupWin('https://paypal.com', 'Connect Paypal Integration', 500, 800);
        if (!authPopupWindow) {
            alert('Please allow popups for this website');
        }
    };

    // PayPal button configuration
    const paypalButton: BannerButton = useMemo(() => {
        if (hasPaypalAccountIssues && isPaypalEmailConfirmed) {
            return {
                name: 'Learn More',
                onClick: () =>
                    window.open(
                        'https://help.stan.store/article/147-why-is-paypal-not-connecting-to-my-stan-store',
                        '_blank'
                    ),
            };
        }
        return { name: 'Update Now', onClick: managePaypalAccount };
    }, [hasPaypalAccountIssues, isPaypalEmailConfirmed]);

    return {
        // Stripe
        hasStripeAccountIssues,
        stripeAccountErrorBannerMessage,
        manageStripeAccount,
        // PayPal
        hasPaypalAccountIssues,
        paypalAccountErrorBannerMessage,
        paypalButton,
        // Instagram
        isErroredIntegration,
        // Products with errored integrations
        hasProductWithErroredIntegration,
        missingIntegrationErrorBannerMessage,
        // Navigation
        navigateToIntegrations,
        navigateToPayments,
        // Loading state
        loading,
    };
}
